import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { LinkResponse, UpdateLinkRequest, ListLinksResponse } from '@readlater/shared';

interface UseUpdateLinkOptions {
  onSuccess?: (link: LinkResponse) => void;
  onError?: (error: Error) => void;
}

export function useUpdateLink(options?: UseUpdateLinkOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: number; updates: UpdateLinkRequest }) => {
      return api.patch<LinkResponse>(`/links/${id}`, updates);
    },

    onMutate: async ({ id, updates }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['link', id] });
      await queryClient.cancelQueries({ queryKey: ['links'] });

      // Snapshot previous values
      const previousLink = queryClient.getQueryData<LinkResponse>(['link', id]);
      const previousAll = queryClient.getQueryData<ListLinksResponse>(['links', 'all']);

      // Optimistically update the single link query
      if (previousLink) {
        queryClient.setQueryData<LinkResponse>(['link', id], {
          ...previousLink,
          ...updates,
        });
      }

      // Optimistically update any list queries
      const updateList = (key: string[]) => {
        queryClient.setQueryData<ListLinksResponse>(key, (old) => {
          if (!old) return old;
          return {
            ...old,
            links: old.links.map((link) => (link.id === id ? { ...link, ...updates } : link)),
          };
        });
      };

      updateList(['links', 'all']);
      updateList(['links', 'unread']);
      updateList(['links', 'favorites']);
      updateList(['links', 'archived']);

      return { previousLink, previousAll };
    },

    onError: (_err, { id }, context) => {
      // Rollback on error
      if (context?.previousLink) {
        queryClient.setQueryData(['link', id], context.previousLink);
      }
      if (context?.previousAll) {
        queryClient.setQueryData(['links', 'all'], context.previousAll);
      }
      options?.onError?.(_err);
    },

    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },

    onSettled: (_data, _error, { id }) => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['link', id] });
      queryClient.invalidateQueries({ queryKey: ['links'] });
    },
  });
}
